import { useEffect, useMemo, useRef, useState } from 'react'
import { useParams, useNavigate, Link as RouterLink } from 'react-router-dom'
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Container,
  Divider,
  IconButton,
  Link as MuiLink,
  Stack,
  Tooltip,
  Typography,
  alpha,
} from '@mui/material'
import {
  IconArrowLeft,
  IconBrandFacebook,
  IconBrandLinkedin,
  IconCalendar,
  IconCheck,
  IconExternalLink,
  IconInfoCircle,
  IconLink,
  IconMail,
  IconNews,
  IconWorld,
} from '@tabler/icons-react'
import { tokens } from '@/theme'

// Industry-news digests (category='industry-news') are the *external* curated
// stream. They render with a distinct gold accent + an up-front neutrality
// banner so visitors never confuse them with our source-cited GSA decision
// digests (per WEEKEND-NEWS-SCOUT-PLAN "two datasets stay separate").
const INDUSTRY_NEWS_CATEGORY = 'industry-news'
const INDUSTRY_NEWS_ACCENT = '#C8A97E'
const INDUSTRY_NEWS_DISCLAIMER =
  'Industry news, curated from public sources and separate from our tracked ' +
  'GSA decision data. Links below go to the original publishers; inclusion is ' +
  'not endorsement.'
import { markdownToHtml, isSafeUrl, isExternalUrl } from '@/utils/markdownToHtml'
import { publicationUrl, linkedinShareUrl, facebookShareUrl } from '@/utils/share'
import { trackFunnel } from '@/utils/analytics'
import { apiUrl } from '@/lib/apiBase'

// ─── Constants ────────────────────────────────────────────────────────────────

/** Maximum publication IDs fetched in a single /publications?ids= request. Must match MAX_IDS in publications.py. */
const MAX_IDS = 50

// ─── Types — canonical publication shape (epic e9 / T-114) ─────────────────────

type PublicationType = 'article' | 'digest'

interface PublicationItem {
  item_id: number
  publication_id: number
  meeting_id: string | null
  member_publication_id: number | null
  position: number | null
}

interface PublicationDetail {
  publication_id: number
  type: PublicationType
  title: string
  summary: string | null
  body: string | null
  status: string
  category: string | null
  source_url: string | null
  source_name: string | null
  read_min: number | null
  gsa_id: number | null
  period_start: string | null
  period_end: string | null
  published_at: string | null
  items: PublicationItem[]
}

interface MemberPublication {
  publication_id: number
  type: PublicationType
  title: string
  summary: string | null
  source_url: string | null
  source_name: string | null
  published_at: string | null
  category: string | null
}

interface PublicationListPage {
  items: MemberPublication[]
  total: number
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatDate(iso: string | null | undefined): string {
  if (!iso) return ''
  try {
    return new Date(iso).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
  } catch {
    return iso
  }
}

function formatPeriod(start: string | null, end: string | null): string {
  if (!start || !end) return ''
  const s = new Date(start).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
  const e = new Date(end).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
  return s === e ? s : `${s} – ${e}`
}

// ─── Inline SPA link interceptor (matches NewsletterDetail) ────────────────────

function useSpaLinkInterceptor(
  containerRef: React.RefObject<HTMLElement | null>,
  navigate: (p: string) => void,
) {
  useEffect(() => {
    const el = containerRef.current
    if (!el) return
    function handleClick(e: MouseEvent) {
      const target = (e.target as HTMLElement).closest('a[data-spa-link="true"]') as HTMLAnchorElement | null
      if (!target) return
      e.preventDefault()
      navigate(target.getAttribute('href') ?? '/')
    }
    el.addEventListener('click', handleClick)
    return () => el.removeEventListener('click', handleClick)
  }, [containerRef, navigate])
}

// ─── Member publication card (digest composition) ──────────────────────────────

function MemberCard({ item }: { item: MemberPublication }) {
  // Member articles link to their canonical publication detail; if they carry an
  // external source we surface it as a secondary link.
  const externalUrl = item.source_url && isSafeUrl(item.source_url) && isExternalUrl(item.source_url)
    ? item.source_url
    : null

  return (
    <Box
      sx={{
        py: 2,
        '&:not(:last-child)': { borderBottom: `1px solid ${alpha(tokens.green.main, 0.1)}` },
      }}
    >
      <MuiLink
        component={RouterLink}
        to={`/news/${item.publication_id}`}
        sx={{
          display: 'block',
          fontWeight: 600,
          fontSize: '0.95rem',
          color: tokens.green.main,
          textDecoration: 'none',
          mb: 0.5,
          '&:hover': { textDecoration: 'underline' },
        }}
      >
        {item.title}
      </MuiLink>

      {item.summary && (
        <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.55, mb: 0.75 }}>
          {item.summary}
        </Typography>
      )}

      <Stack direction="row" spacing={1.5} useFlexGap sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
        {item.published_at && (
          <Stack direction="row" spacing={0.4} sx={{ alignItems: 'center', color: 'text.secondary' }}>
            <IconCalendar size={12} />
            <Typography variant="caption" sx={{ fontFamily: '"DM Mono", monospace', letterSpacing: '0.03em' }}>
              {formatDate(item.published_at)}
            </Typography>
          </Stack>
        )}
        {item.category && (
          <Typography
            variant="caption"
            sx={{
              color: tokens.green.main,
              fontFamily: '"DM Mono", monospace',
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: '0.06em',
              fontSize: '0.65rem',
            }}
          >
            {item.category}
          </Typography>
        )}
        {externalUrl && (
          <MuiLink
            href={externalUrl}
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.4,
              fontSize: '0.72rem',
              color: 'text.secondary',
              fontFamily: '"DM Mono", monospace',
            }}
          >
            Source <IconExternalLink size={11} />
          </MuiLink>
        )}
      </Stack>
    </Box>
  )
}

// ─── Share controls ───────────────────────────────────────────────────────────

/**
 * LinkedIn / Facebook / Copy-link controls for a publication, rendered on the
 * dark hero. Uses plain share-intent URLs (no SDK, no credentials); the rich
 * preview card these links unfurl into is produced by the OG prerender (T-129).
 */
function ShareControls({ id }: { id: number }) {
  const [copied, setCopied] = useState(false)
  const url = publicationUrl(id)

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(url)
      // Privacy-safe funnel: record only the share channel, never which
      // article (the publication id is not transmitted). (T-147)
      trackFunnel('share', { surface: 'publication_detail', channel: 'copy_link' })
      setCopied(true)
      window.setTimeout(() => setCopied(false), 2000)
    } catch {
      // Clipboard unavailable (e.g. insecure context) — silently no-op.
    }
  }

  const iconSx = { color: alpha('#fff', 0.85), '&:hover': { color: '#fff' } }

  return (
    <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center', mt: 2.5 }}>
      <Typography
        variant="caption"
        sx={{ color: alpha('#fff', 0.6), mr: 0.5, letterSpacing: '0.06em' }}
      >
        Share
      </Typography>
      <Tooltip title="Share on LinkedIn">
        <IconButton
          component="a"
          href={linkedinShareUrl(url)}
          target="_blank"
          rel="noopener"
          size="small"
          aria-label="Share on LinkedIn"
          onClick={() => trackFunnel('share', { surface: 'publication_detail', channel: 'linkedin' })}
          sx={iconSx}
        >
          <IconBrandLinkedin size={18} />
        </IconButton>
      </Tooltip>
      <Tooltip title="Share on Facebook">
        <IconButton
          component="a"
          href={facebookShareUrl(url)}
          target="_blank"
          rel="noopener"
          size="small"
          aria-label="Share on Facebook"
          onClick={() => trackFunnel('share', { surface: 'publication_detail', channel: 'facebook' })}
          sx={iconSx}
        >
          <IconBrandFacebook size={18} />
        </IconButton>
      </Tooltip>
      <Tooltip title={copied ? 'Link copied' : 'Copy link'}>
        <IconButton
          onClick={copyLink}
          size="small"
          aria-label="Copy link to this article"
          sx={iconSx}
        >
          {copied ? <IconCheck size={18} /> : <IconLink size={18} />}
        </IconButton>
      </Tooltip>
    </Stack>
  )
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function PublicationDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()

  const [pub, setPub] = useState<PublicationDetail | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const [members, setMembers] = useState<MemberPublication[]>([])
  const [membersLoading, setMembersLoading] = useState(false)
  const [membersError, setMembersError] = useState<string | null>(null)

  const articleRef = useRef<HTMLElement | null>(null)
  useSpaLinkInterceptor(articleRef, navigate)

  // Fetch the publication.
  useEffect(() => {
    if (!id) return
    const controller = new AbortController()
    setLoading(true)
    setError(null)

    fetch(apiUrl(`/api/agro/v1/publications/${id}`), { signal: controller.signal })
      .then(res => {
        if (res.status === 404) throw new Error('not_found')
        if (!res.ok) throw new Error(`HTTP ${res.status}`)
        return res.json() as Promise<PublicationDetail>
      })
      .then(data => setPub(data))
      .catch(err => {
        if ((err as Error).name === 'AbortError') return
        if ((err as Error).message === 'not_found') {
          navigate('/news', { replace: true })
        } else {
          setError('Failed to load article. Please try again.')
        }
      })
      .finally(() => setLoading(false))

    return () => controller.abort()
  }, [id, navigate])

  // For digests: fetch member publications via the canonical relations (NOT
  // copied content) so the digest renders its composed articles live.
  const publicationId = pub?.publication_id
  const isDigest = pub?.type === 'digest'
  useEffect(() => {
    if (!pub || pub.type !== 'digest') return

    const memberIds = Array.from(
      new Set(
        pub.items
          .map(it => it.member_publication_id)
          .filter((n): n is number => n !== null),
      ),
    ).slice(0, MAX_IDS)

    if (memberIds.length === 0) return

    const controller = new AbortController()
    setMembersLoading(true)
    setMembersError(null)

    const params = new URLSearchParams()
    memberIds.forEach(mid => params.append('ids', String(mid)))
    params.set('page_size', String(memberIds.length))

    fetch(apiUrl(`/api/agro/v1/publications?${params.toString()}`), { signal: controller.signal })
      .then(res => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`)
        return res.json() as Promise<PublicationListPage>
      })
      .then(data => {
        // Preserve digest ordering from pub.items.
        const byId = new Map(data.items.map(m => [m.publication_id, m]))
        const ordered = memberIds
          .map(mid => byId.get(mid))
          .filter((m): m is MemberPublication => m !== undefined)
        setMembers(ordered)
      })
      .catch(err => {
        if ((err as Error).name === 'AbortError') return
        setMembersError('Could not load the digest items.')
      })
      .finally(() => setMembersLoading(false))

    return () => controller.abort()
  // publicationId is the stable primitive; pub.items is read inside.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [publicationId, isDigest])

  const bodyHtml = useMemo(
    () => (pub?.body ? markdownToHtml(pub.body) : ''),
    [pub?.body],
  )

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 16 }}>
        <CircularProgress size={36} sx={{ color: tokens.green.main }} />
      </Box>
    )
  }

  if (error) {
    return (
      <Container maxWidth="md" sx={{ py: 10, textAlign: 'center' }}>
        <Alert severity="error" sx={{ mb: 3, textAlign: 'left' }}>{error}</Alert>
        <Button variant="outlined" color="primary" onClick={() => navigate('/news')}>
          Back to News
        </Button>
      </Container>
    )
  }

  if (!pub) return null

  const digest = pub.type === 'digest'
  // Industry-news roundups are digests in the dedicated category — rendered as a
  // distinct external stream (gold accent + neutrality banner), never as a
  // green source-cited GSA decision digest.
  const isIndustryNews = digest && pub.category === INDUSTRY_NEWS_CATEGORY
  const accent = isIndustryNews ? INDUSTRY_NEWS_ACCENT : digest ? tokens.green.main : tokens.blue.main
  const hasExternalSource = !digest && pub.source_url && isSafeUrl(pub.source_url) && isExternalUrl(pub.source_url)

  return (
    <Box sx={{ background: tokens.cream, minHeight: '100vh' }}>
      {/* ── Hero ─────────────────────────────────────────────────────────── */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${tokens.green.dark} 0%, ${accent} 100%)`,
          color: '#fff',
          pt: { xs: 8, md: 11 },
          pb: { xs: 6, md: 8 },
        }}
      >
        <Container maxWidth="md">
          <Button
            size="small"
            startIcon={<IconArrowLeft size={15} />}
            component={RouterLink}
            to="/news"
            sx={{
              mb: 3,
              color: alpha('#fff', 0.75),
              fontSize: '0.8rem',
              fontFamily: '"DM Mono", monospace',
              textTransform: 'none',
              px: 0,
              minWidth: 0,
              '&:hover': { color: '#fff', background: 'transparent' },
            }}
          >
            All News
          </Button>

          <Typography
            variant="overline"
            sx={{ color: alpha('#fff', 0.6), display: 'block', mb: 1, letterSpacing: '0.15em' }}
          >
            <Stack component="span" direction="row" spacing={1} sx={{ alignItems: 'center' }}>
              {isIndustryNews ? <IconWorld size={13} /> : digest ? <IconMail size={13} /> : <IconNews size={13} />}
              <span>
                {isIndustryNews ? 'Industry News' : digest ? 'Digest' : 'Article'}
                {digest && pub.period_start && pub.period_end
                  ? ` · ${formatPeriod(pub.period_start, pub.period_end)}`
                  : ''}
                {!digest && pub.category ? ` · ${pub.category}` : ''}
              </span>
            </Stack>
          </Typography>

          <Typography
            variant="h2"
            component="h1"
            sx={{

              fontSize: { xs: '1.8rem', md: '2.6rem' },
              fontWeight: 700,
              lineHeight: 1.2,
              mb: 2,
            }}
          >
            {pub.title}
          </Typography>

          {pub.published_at && (
            <Stack direction="row" spacing={0.6} sx={{ alignItems: 'center', color: alpha('#fff', 0.65) }}>
              <IconCalendar size={14} />
              <Typography variant="caption" sx={{ fontFamily: '"DM Mono", monospace', letterSpacing: '0.06em' }}>
                Published {formatDate(pub.published_at)}
                {pub.read_min ? ` · ${pub.read_min} min read` : ''}
              </Typography>
            </Stack>
          )}

          <ShareControls id={pub.publication_id} />
        </Container>
      </Box>

      {/* ── Main content ─────────────────────────────────────────────────── */}
      <Container maxWidth="md" sx={{ py: { xs: 5, md: 8 } }}>
        <Card sx={{ mb: 5, border: `1px solid ${alpha(tokens.green.main, 0.1)}`, borderRadius: 3, boxShadow: 'none' }}>
          <CardContent sx={{ p: { xs: 3, md: 5 } }}>
            {/* Neutrality disclaimer — surfaced up front for industry-news so the
                external curated stream is never confused with our source-cited
                GSA decision data (T-189). */}
            {isIndustryNews && (
              <Box
                role="note"
                aria-label="Industry news neutrality notice"
                sx={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 1.25,
                  mb: 3,
                  px: 2,
                  py: 1.75,
                  borderRadius: 2,
                  bgcolor: alpha(INDUSTRY_NEWS_ACCENT, 0.1),
                  border: `1px solid ${alpha(INDUSTRY_NEWS_ACCENT, 0.4)}`,
                }}
              >
                <IconInfoCircle size={18} style={{ color: '#7A5C2E', flexShrink: 0, marginTop: 2 }} />
                <Typography variant="body2" sx={{ color: '#7A5C2E', lineHeight: 1.55 }}>
                  {INDUSTRY_NEWS_DISCLAIMER}
                </Typography>
              </Box>
            )}

            {pub.summary && (
              <Typography
                variant="h6"
                sx={{
                  fontFamily: '"Syne", system-ui, sans-serif',
                  fontWeight: 500,
                  color: 'text.secondary',
                  lineHeight: 1.6,
                  mb: 3,
                }}
              >
                {pub.summary}
              </Typography>
            )}

            {/* Body — articles render their body; digests render their preamble.
                Articles with no body (legacy news items) fall back to source. */}
            {bodyHtml ? (
              <Box
                ref={articleRef}
                component="article"
                dangerouslySetInnerHTML={{ __html: bodyHtml }}
                sx={{
                  fontFamily: '"Poppins", system-ui, sans-serif',
                  fontSize: '1rem',
                  lineHeight: 1.8,
                  color: tokens.textDark,
                  '& h1, & h2, & h3, & h4, & h5, & h6': {

                    fontWeight: 700,
                    lineHeight: 1.25,
                    mt: 3,
                    mb: 1.5,
                    color: tokens.green.dark,
                  },
                  '& h1': { fontSize: '1.8rem' },
                  '& h2': { fontSize: '1.5rem' },
                  '& h3': { fontSize: '1.25rem' },
                  '& h4, & h5, & h6': { fontSize: '1rem' },
                  '& p': { mb: 2 },
                  '& ul, & ol': { pl: 3, mb: 2 },
                  '& li': { mb: 0.5 },
                  '& a': {
                    color: tokens.green.main,
                    textDecoration: 'none',
                    fontWeight: 500,
                    '&:hover': { textDecoration: 'underline' },
                  },
                  '& code': {
                    fontFamily: '"DM Mono", monospace',
                    fontSize: '0.88em',
                    background: alpha(tokens.green.main, 0.07),
                    color: tokens.green.dark,
                    px: '4px',
                    py: '2px',
                    borderRadius: '4px',
                  },
                  '& pre': {
                    background: tokens.green[900],
                    color: tokens.green[50],
                    fontFamily: '"DM Mono", monospace',
                    fontSize: '0.85rem',
                    p: 3,
                    borderRadius: 2,
                    overflowX: 'auto',
                    mb: 2,
                    '& code': { background: 'transparent', color: 'inherit', px: 0, py: 0 },
                  },
                  '& blockquote': {
                    borderLeft: `4px solid ${tokens.green.main}`,
                    pl: 2.5,
                    ml: 0,
                    my: 2,
                    color: 'text.secondary',
                    fontStyle: 'italic',
                  },
                  '& hr': { border: 'none', borderTop: `1px solid ${alpha(tokens.green.main, 0.15)}`, my: 3 },
                  '& strong': { fontWeight: 700 },
                  '& em': { fontStyle: 'italic' },
                }}
              />
            ) : (
              !digest && (
                <Typography variant="body1" sx={{ color: 'text.secondary' }}>
                  {hasExternalSource ? (
                    <>
                      Read the full article at{' '}
                      <MuiLink href={pub.source_url!} target="_blank" rel="noopener noreferrer">
                        {pub.source_name ?? pub.source_url}
                      </MuiLink>
                      .
                    </>
                  ) : (
                    'No content available.'
                  )}
                </Typography>
              )
            )}
          </CardContent>
        </Card>

        {/* ── Digest composition — member publications via canonical relations ── */}
        {digest && (membersLoading || membersError || members.length > 0) && (
          <Box sx={{ mb: 6 }}>
            <Stack direction="row" spacing={1} sx={{ alignItems: 'center', mb: 2.5 }}>
              <IconNews size={18} color={tokens.green.main} />
              <Typography variant="h5" sx={{ fontWeight: 700, color: tokens.green.dark }}>
                In This Digest
              </Typography>
            </Stack>
            <Divider sx={{ mb: 0 }} />

            {membersLoading && (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}>
                <CircularProgress size={28} sx={{ color: tokens.green.main }} />
              </Box>
            )}
            {membersError && <Alert severity="warning" sx={{ mt: 2 }}>{membersError}</Alert>}
            {!membersLoading && !membersError && members.length > 0 && (
              <Box>
                {members.map(m => (
                  <MemberCard key={m.publication_id} item={m} />
                ))}
              </Box>
            )}
          </Box>
        )}

        {/* ── External source (articles) ───────────────────────────────────── */}
        {hasExternalSource && bodyHtml && (
          <Box sx={{ mb: 5 }}>
            <MuiLink
              href={pub.source_url!}
              target="_blank"
              rel="noopener noreferrer"
              sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, color: tokens.green.main, fontWeight: 600 }}
            >
              View original source{pub.source_name ? ` · ${pub.source_name}` : ''}
              <IconExternalLink size={14} />
            </MuiLink>
          </Box>
        )}

        {/* ── Footer nav ───────────────────────────────────────────────────── */}
        <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
          <Button
            variant="outlined"
            color="primary"
            startIcon={<IconArrowLeft size={16} />}
            component={RouterLink}
            to="/news"
          >
            Back to News
          </Button>
          <Typography variant="caption" sx={{ color: 'text.secondary', fontFamily: '"DM Mono", monospace' }}>
            {isIndustryNews ? 'Industry News' : digest ? 'Digest' : 'Article'} #{pub.publication_id}
          </Typography>
        </Stack>
      </Container>
    </Box>
  )
}
